import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const HOVER_CLOSE_DELAY = 140;

const useHeaderShopMenu = (pathname: string, search: string) => {
  const [shopOpen, setShopOpen] = useState(false);
  const [shopGender, setShopGender] = useState('');
  const [usesHoverMenu, setUsesHoverMenu] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentParams = useMemo(() => new URLSearchParams(search), [search]);
  const currentGender = currentParams.get('gender') || '';
  const currentType = currentParams.get('type') || '';

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openShopMenu = useCallback(() => {
    clearCloseTimer();
    setShopOpen(true);
  }, [clearCloseTimer]);

  const closeShopMenu = useCallback(() => {
    clearCloseTimer();
    setShopOpen(false);
  }, [clearCloseTimer]);

  const scheduleShopClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setShopOpen(false);
      closeTimerRef.current = null;
    }, HOVER_CLOSE_DELAY);
  }, [clearCloseTimer]);

  const toggleShopMenu = useCallback(() => {
    setShopOpen((open) => !open);
  }, []);

  useEffect(() => {
    closeShopMenu();
  }, [closeShopMenu, pathname, search]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const syncHoverMode = () => setUsesHoverMenu(hoverQuery.matches);

    syncHoverMode();
    hoverQuery.addEventListener('change', syncHoverMode);

    return () => hoverQuery.removeEventListener('change', syncHoverMode);
  }, []);

  useEffect(() => {
    if (currentGender === 'womens' || currentGender === 'mens' || currentGender === '') {
      setShopGender(currentGender);
    }
  }, [currentGender]);

  useEffect(() => {
    window.addEventListener('jew:search-open', closeShopMenu);

    return () => window.removeEventListener('jew:search-open', closeShopMenu);
  }, [closeShopMenu]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return {
    shopOpen,
    shopGender,
    usesHoverMenu,
    currentGender,
    currentType,
    setShopGender,
    openShopMenu,
    closeShopMenu,
    scheduleShopClose,
    toggleShopMenu,
  };
};

export default useHeaderShopMenu;
